// Catalogi per taal met terugval op Nederlands (golf 3, talen). Een taalbestand is een gedeeltelijke
// kopie van de Nederlandse vorm (`LocalePartial<T>`): wat ontbreekt of leeg is, komt uit nl. Zo
// toont het bord nooit een rauwe sleutel, en kunnen vertalers per domeinbestand (design.tr.ts,
// app.ar.ts, …) los van elkaar werken. `missingKeys` zegt per taal wat nog ontbreekt.
/** Taal van een meervoudsobject uit een taalbestand (t() kiest er de regels mee). */
const pluralLocales = new WeakMap();
export function pluralLocaleOf(forms) {
    return pluralLocales.get(forms);
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isText(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
/** Een meervoudsobject in nl: precies `one` en `other`. */
export function isPluralShape(value) {
    if (!isRecord(value))
        return false;
    const keys = Object.keys(value);
    return (keys.length === 2 &&
        typeof value.one === 'string' &&
        typeof value.other === 'string' &&
        keys.indexOf('one') >= 0 &&
        keys.indexOf('other') >= 0);
}
const PLURAL_KEYS = ['zero', 'one', 'two', 'few', 'many', 'other'];
function validPlural(value) {
    if (!isRecord(value) || !isText(value.one) || !isText(value.other))
        return false;
    return Object.keys(value).every((k) => PLURAL_KEYS.indexOf(k) >= 0 && isText(value[k]));
}
function validList(value, length) {
    return Array.isArray(value) && value.length === length && value.every(isText);
}
/** Lijst van teksten (weekdagen, maanden): alles of niets. Een lijst van blokken: per blok. */
function isTextList(value) {
    return value.every((v) => typeof v === 'string');
}
/**
 * Legt `partial` over `base` (nl). Alleen geldige waarden tellen: een lege tekst, een lijst met de
 * verkeerde lengte of een meervoud zonder `one`/`other` valt terug op nl. Onbekende sleutels in het
 * taalbestand worden genegeerd (de test `extraKeys` vangt tikfouten).
 */
export function mergeCatalog(base, partial, locale) {
    var _a;
    if (typeof base === 'string')
        return (isText(partial) ? partial : base);
    if (Array.isArray(base)) {
        if (isTextList(base))
            return (validList(partial, base.length) ? partial : base);
        const items = Array.isArray(partial) && partial.length === base.length ? partial : [];
        return base.map((item, i) => mergeCatalog(item, items[i], locale));
    }
    if (isPluralShape(base)) {
        if (!validPlural(partial))
            return base;
        const copy = {};
        const forms = partial;
        for (const k of Object.keys(forms))
            copy[k] = (_a = forms[k]) !== null && _a !== void 0 ? _a : '';
        pluralLocales.set(copy, locale);
        return copy;
    }
    if (isRecord(base)) {
        if (!isRecord(partial))
            return base;
        const out = {};
        for (const key of Object.keys(base))
            out[key] = mergeCatalog(base[key], partial[key], locale);
        return out;
    }
    return base;
}
/** Paden (a.b.c) die in `partial` ontbreken of ongeldig zijn; `ignore` = paden die niet tellen. */
export function missingKeys(base, partial, ignore = [], prefix = '') {
    if (ignore.indexOf(prefix.replace(/\.$/, '')) >= 0)
        return [];
    const here = prefix.replace(/\.$/, '');
    if (typeof base === 'string')
        return isText(partial) ? [] : [here];
    if (Array.isArray(base)) {
        if (isTextList(base))
            return validList(partial, base.length) ? [] : [here];
        const items = Array.isArray(partial) && partial.length === base.length ? partial : [];
        const out = [];
        base.forEach((item, i) => {
            out.push(...missingKeys(item, items[i], ignore, `${prefix}${i}.`));
        });
        return out;
    }
    if (isPluralShape(base))
        return validPlural(partial) ? [] : [here];
    if (isRecord(base)) {
        const sub = isRecord(partial) ? partial : undefined;
        const out = [];
        for (const key of Object.keys(base)) {
            out.push(...missingKeys(base[key], sub ? sub[key] : undefined, ignore, `${prefix}${key}.`));
        }
        return out;
    }
    return [];
}
/** Sleutels in het taalbestand die nl niet kent (tikfout of verouderd): altijd een fout. */
export function extraKeys(base, partial, prefix = '') {
    if (Array.isArray(base) && Array.isArray(partial)) {
        if (isTextList(base))
            return [];
        const out = [];
        partial.forEach((item, i) => {
            out.push(...extraKeys(base[i], item, `${prefix}${i}.`));
        });
        return out;
    }
    if (!isRecord(partial))
        return [];
    if (isPluralShape(base)) {
        return Object.keys(partial)
            .filter((k) => PLURAL_KEYS.indexOf(k) < 0)
            .map((k) => `${prefix}${k}`);
    }
    if (!isRecord(base))
        return [];
    const out = [];
    for (const key of Object.keys(partial)) {
        if (!(key in base))
            out.push(`${prefix}${key}`);
        else
            out.push(...extraKeys(base[key], partial[key], `${prefix}${key}.`));
    }
    return out;
}
/** Aantal teksten (bladeren) in een catalogus, voor het overzicht per taal. */
export function countKeys(base) {
    if (typeof base === 'string' || isPluralShape(base))
        return 1;
    if (Array.isArray(base)) {
        return isTextList(base) ? 1 : base.reduce((n, item) => n + countKeys(item), 0);
    }
    if (isRecord(base))
        return Object.keys(base).reduce((n, k) => n + countKeys(base[k]), 0);
    return 0;
}
//# sourceMappingURL=merge.js.map